import { isAthlete, isCoach } from '@/helpers/rolesHelper';
import { isDayOffSession, isMultisportSession, isWaterSession } from '@/helpers/sessionHelper';
import get from 'get-object-path';

export const sessionDetails = {
    namespaced: true,

    state: {
        session: null,
        healthStatuses: []
    },

    mutations: {
        emptySession(state) {
            state.session = null;
        },

        sessionSave(state, data) {
            state.session = data;
        },

        healthStatuses(state, data) {
            state.healthStatuses = [...data];
        }
    },

    getters: {
        sessionHealthStatuses(state) {
            return state.healthStatuses;
        },

        session(state) {
            return state.session;
        },

        sessionId(state) {
            return state.session.id;
        },

        sessionIntensities(state) {
            return get(state.session, 'intensities.data') || [];
        },

        sessionHrIntensities(state) {
            return get(state.session, 'heart_rate_intensities.data') || [];
        },

        sessionStrokeRateIntensities(state) {
            return get(state.session, 'stroke_rate_intensities.data') || [];
        },

        participants(state) {
            const participants = get(state.session, 'participants.data') || [];
            return _.sortBy(participants, 'user.data.full_name');
        },

        isEvent(state) {
            return !get(state, 'session.session_id');
        },

        sportId(state) {
            return get(state, 'session.sport.id');
        },

        sportName(state) {
            return get(state, 'session.sport.name');
        },

        canEditSession(state, getters, rootState, rootGetters) {
            return (
                get(state, 'session.user_actions.data.edit') || rootGetters['userPermissions/canViewEditAllSessions']
            );
        },

        canAddAllResults(state) {
            return get(state, 'session.user_actions.data.add_all_scores');
        },

        activeSport(state, getters) {
            return !isDayOffSession(getters.sportId) && !isMultisportSession(getters.sportId);
        },

        showAddScoreButton(state, getters) {
            if (!getters.eligibleToAddScore || getters.isEvent) return false;
            if (isWaterSession(getters.sportId)) return getters.canAddAllResults;
            return getters.userIsAthlete || getters.canAddAllResults;
        },

        eligibleToAddScore(state, getters) {
            return !isDayOffSession(getters.sportId) && !isMultisportSession(getters.sportId);
        },

        userSessionRoleId(state) {
            return get(state, 'session.attendance.data.user_role.data.id');
        },

        userIsAthlete(state, getters) {
            return isAthlete(getters.userSessionRoleId);
        },

        userIsCoach(state, getters) {
            return isCoach(getters.userSessionRoleId);
        },

        showImportData(state, getters) {
            return (
                !isDayOffSession(getters.sportId) &&
                !getters.isEvent &&
                (getters.userIsAthlete || isWaterSession(getters.sportId))
            );
        }
    },

    actions: {
        getSession(context, payload) {
            return context.dispatch(
                'get',
                {
                    url: api + 'session/' + payload.id,
                    params: {
                        include: ['session,user_actions,attendance.training_load_score,venue', payload.include]
                            .filter(Boolean)
                            .join()
                    }
                },
                { root: true }
            );
        },

        getSessionDetails({ dispatch }, { id }) {
            return dispatch(
                'get',
                {
                    url: `${api}session/${id}`,
                    params: {
                        include: [
                            'attendance.training_load_score',
                            'creator:fields(full_name|created_at)',
                            'crews:fields(age|connect_url|gender|id|name|boat_type|weight)',
                            'crews.gmt_score',
                            'crews.crew_users:fields(user_id)',
                            'crews.crew_users.user:fields(id|first_name|last_name|full_name|picture)',
                            'crews.crew_users.user.crew_user_groups',
                            'crews.inventory.usage_history',
                            'club:fields(name)',
                            'focuses',
                            'intensities',
                            'last_modified_by:fields(full_name|created_at)',
                            'participants:fields(id|user_id|user_role_id|attendance|ill|injured|did_alternative)',
                            'participants.user:fields(id|first_name|last_name|full_name|picture)',
                            'session',
                            'session.structure.athletes:fields(id)',
                            'session.structure.pieces.crews:order(created_at|desc)',
                            'session.structure.pieces.crews:fields(age|connect_url|gender|id|name|boat_type|weight)',
                            'session.structure.pieces.crews.crew_users:fields(user_id)',
                            'session.structure.pieces.crews.crew_users.user:fields(id|first_name|last_name|full_name|picture)',
                            'session.structure.pieces.crews.crew_users.user.crew_user_groups',
                            'session.structure.pieces.crews.gmt_score',
                            'session.structure.pieces.crews.inventory.usage_history',
                            'sport_crew_results.crews:fields(id|name|age)',
                            'sport_crew_results.crews.crew_users:fields(user_id)',
                            'statistics',
                            'tags',
                            'user_actions',
                            'user_sport_results:fields(id)',
                            'user_sport_results.sport.overall_results:fields(id)',
                            'user_sport_results.sport.pieces:fields(id)',
                            'user_sport_results.sport.pieces.segments:fields(id)',
                            'venue',
                            'session_files.crews.session_crew_files.additional_data',
                            'session_files.crews.telemetry_files'
                        ].join(',')
                    }
                },
                { root: true }
            );
        },

        getEventDetails({ dispatch }, { id }) {
            return dispatch(
                'get',
                {
                    url: `${api}session/${id}?fields=id|name|location|start_date|end_date|updated_at|all_day_event|club_id|sport|created_at|description|attendance_limit`,
                    params: {
                        include: [
                            'attendance',
                            'creator:fields(full_name|created_at)',
                            'club:fields(name)',
                            'last_modified_by:fields(full_name|created_at)',
                            'participants:fields(id|user_id|user_role_id|attendance|ill|injured|did_alternative)',
                            'participants.user:fields(id|first_name|last_name|full_name|picture)',
                            'statistics',
                            'tags',
                            'user_actions',
                            'venue'
                        ].join(',')
                    }
                },
                { root: true }
            );
        },

        getHealthStatuses(context) {
            return context.dispatch(
                'get',
                {
                    url: api + 'not-attending-heath-options'
                },
                { root: true }
            );
        }
    }
};

export default sessionDetails;
